import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { PosterCard } from "@/components/rawan/PosterCard";
import { filterByCategory, getAllCategories } from "@/lib/content/loader";
import { useAllItems } from "@/lib/library-store";
import { useTheme } from "@/lib/theme-context";

export const Route = createFileRoute("/categories/$slug")({
  loader: ({ params }) => ({ slug: params.slug }),
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Rawan UI` },
      { property: "og:url", content: `/categories/${params.slug}` },
    ],
    links: [{ rel: "canonical", href: `/categories/${params.slug}` }],
  }),
  component: CategoryDetail,
});

function CategoryDetail() {
  const { slug } = Route.useLoaderData();
  const { language } = useTheme();
  const t = (ar: string, en: string, fr: string) =>
    language === "ar" ? ar : language === "fr" ? fr : en;
  const cat = getAllCategories().find((c) => c.slug === slug);
  const fromContent = filterByCategory(slug);
  const all = useAllItems();
  const slugify = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const extra = all.filter((m) =>
    (m.genresEn ?? []).some((g) => slugify(g) === slug),
  );
  const matchedSlugs = new Set([
    ...fromContent.movies.map((m) => m.slug),
    ...fromContent.series.map((s) => s.slug),
  ]);
  const items = [
    ...all.filter((m) => matchedSlugs.has(m.slug)),
    ...extra.filter((m) => !matchedSlugs.has(m.slug)),
  ];
  const title = cat ? (language === "ar" ? cat.ar : language === "fr" ? cat.fr : cat.en) : slug;
  const alt = cat ? (language === "ar" ? cat.en : language === "fr" ? cat.en : cat.ar) : "";
  const itemsLabel = t("عنصر", "items", "éléments");

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-10">
        <header className="mb-8">
          <Link to="/categories" className="text-sm text-muted-foreground hover:text-primary">
            ← {t("التصنيفات", "Categories", "Catégories")}
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold mt-2">{title}</h1>
          <p className="text-muted-foreground mt-1">
            {items.length} {itemsLabel}{alt ? ` · ${alt}` : ""}
          </p>
        </header>
        {items.length === 0 ? (
          <p className="text-muted-foreground">
            {t(
              "لا يوجد محتوى في هذا التصنيف بعد.",
              "No content in this category yet.",
              "Aucun contenu dans cette catégorie pour le moment.",
            )}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {items.map((m) => (
              <PosterCard key={m.id} movie={m} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
